import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getPermissionValues = async (token, permissionId) => {
  const url = `admin/permissions/${permissionId}/permission_values`;
  const data = await apiGet({ url, token });
  return data;
};

const getPermissionValue = async (token, id) => {
  const url = `admin/permission_values/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createPermissionValue = async (token, permissionId, params) => {
  const url = `admin/permissions/${permissionId}/permission_values`;
  const data = await apiPost({ url, token, objectName: 'permission_value', params });
  return data;
};

const updatePermissionValue = async (token, id, params) => {
  const url = `admin/permission_values/${id}`;
  const data = await apiPatch({ url, token, objectName: 'permission_value', params });
  return data;
};

const deletePermissionValue = async (token, id) => {
  const url = `admin/permission_values/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminPermissionValues({ permissionId, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getPermissionValues(token, permissionId);
  const queryKey = ['admin-permission-values', permissionId];
  return useQuery(queryKey, queryFn, { enabled: !!permissionId, ...options });
}

export function useAdminPermissionValue({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getPermissionValue(token, id);
  const queryKey = ['admin-permission-value', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminPermissionValue({ permissionId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createPermissionValue(token, permissionId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-permission-values', permissionId]);
        queryClient.invalidateQueries(['admin-permission', permissionId]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminPermissionValue({ id, permissionId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updatePermissionValue(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-permission-values', permissionId]);
        queryClient.invalidateQueries(['admin-permission', permissionId]);
        queryClient.invalidateQueries(['admin-permission-value', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminPermissionValue({ permissionId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deletePermissionValue(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-permission-values', permissionId]);
        queryClient.invalidateQueries(['admin-permission', permissionId]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
